/**
 * Supplier quotations — `quoflow.quoflow.quoflow.api.supplier_quotation.*`, Task 9 of the
 * migration. Live shapes verified against rfqdev.etoserp.com, 2026-08-04 (see the task
 * dispatch); follows the pattern `useEnquiries.ts`/`useMail.ts` set: a `Wire*` interface next to
 * each hook, a pure exported `toX` converter, no shared mapper file.
 *
 * Replaces the OLD Frappe-transport `useSupplierQuotations` (`getDoc`/`getList` against the
 * generic `Supplier Quotation` doctype gateway, `mappers/supplierQuotation.ts`) with the new
 * two-endpoint pair `get_supplier_quotations`/`get_supplier_quotation`.
 *
 * `mappers/supplierQuotation.ts` is NOT deleted here. `useComparison.ts` (Task 10) still reads
 * through the OLD `getDoc`/`getList` transport and depends on that file's `SQ_DOCTYPE`/
 * `toSupplierQuotation`/`toSqFxRate` exports — deleting it now would break an unmigrated screen
 * mid-flight. This file supersedes it for everything Task 9 owns; task-9-report.md flags the
 * leftover file for Task 10/12 to finish retiring.
 *
 * ---- uom_mismatch and rfq_uom are REAL now, not derived ----
 *
 * The old mapper computed `uom_mismatch` client-side (`sq_uom !== rfq_uom`) and approximated
 * `rfq_uom` from the row's `stock_uom`, because the raw `Supplier Quotation Item` doctype has
 * neither field (see that file's header, points 1–2 — still true, and still why the OLD
 * transport needs the derivation). `get_supplier_quotations`/`get_supplier_quotation` send BOTH
 * explicitly: the server holds both the RFQ and the reply, so IT decides, not a stock-UOM proxy
 * that was simply wrong whenever the RFQ asked in a non-stock UOM (a common case). `toSupplierQuotationItem`
 * below just reads what the server sends — no derivation of any kind.
 *
 * ---- rate_in_customer_currency, not a client-side FX product ----
 *
 * This wire carries no `base_rate` (the OLD transport's company-currency figure) — instead it
 * sends `rate_in_customer_currency`, the server's own conversion using the document's own
 * exchange rate and rounding. Read as-is; nothing here multiplies `rate` by a client-held FX
 * rate, which would silently drift from whatever total the server actually persisted. See
 * `SupplierQuotationItem.rate_in_customer_currency`'s own comment in `types.ts` for the one real
 * gap this has: the response carries no `customer_currency` field to label the amount with.
 *
 * ---- supplier identity (task 8b's item for this file, now closed) ----
 *
 * `SupplierQuotation.supplier` is the `Supplier` Link value now (a code), not the display name
 * the old mapper preferred — `get_supplier_quotations`' row `supplier` field IS the docname, and
 * there is no `supplier_name` field on this wire to prefer over it. A caller that wants a
 * display name resolves one through the paired RFQ instead (`Rfq.supplier_name`) — see
 * `lib/sq.ts`'s `pairRepliesToRfqs`, fixed by this same task.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { callGet, callPost } from './bosun'
import { BosunApiError } from './errors'
import { bool01, money, num, str } from './wire'
import { getBaseCurrency } from './baseCurrency'
import { qk } from './keys'
import { POLL_MS } from './polling'
import type {
  CreateSupplierQuotationPayload,
  ParseSupplierReplyPayload,
  ParseSupplierReplyResult,
  SupplierQuotation,
  SupplierQuotationItem,
} from '../types'

/** Dev stand-in for the `sq.received` event; a scheduler parses replies server-side every two
 * minutes (task dispatch), so this just needs to catch up shortly after, not in real time. */
export const SQ_POLL_MS = POLL_MS

/* ============================================================================================
 * supplier_quotation.get_supplier_quotations / get_supplier_quotation → toSupplierQuotation
 * ============================================================================================ */

export interface WireSupplierQuotationItem {
  item_code: string
  qty: number
  uom: string
  rfq_uom: string
  uom_mismatch: number | boolean
  rate: number
  /** See `SupplierQuotationItem.rate_in_customer_currency`'s comment (`types.ts`) for the
   * currency-label gap this field carries. */
  rate_in_customer_currency?: number
  remark?: string | null
  enquiry_row_no: string
  opportunity_item?: string | null
}

export interface WireSupplierQuotation {
  name: string
  rfq: string | null
  supplier: string
  status: string
  currency: string
  grand_total?: number
  items: WireSupplierQuotationItem[]
}


/** Only `'Submitted'` reads as a received reply; every other status is still pending — same
 * vocabulary the OLD mapper used (`mappers/supplierQuotation.ts`'s `toSqStatus`). */
function toSqStatus(value: unknown): SupplierQuotation['status'] {
  return value === 'Submitted' ? 'received' : 'pending'
}

function toRfqLink(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

function toSupplierQuotationItem(w: WireSupplierQuotationItem, currency: string): SupplierQuotationItem {
  return {
    item_code: str(w.item_code),
    rfq_uom: str(w.rfq_uom),
    sq_uom: str(w.uom),
    uom_mismatch: bool01(w.uom_mismatch),
    rate: money(w.rate, currency),
    // Left `undefined`, not a fabricated 0, when the server omits it — same "honest absence"
    // choice the old mapper made for a missing `base_rate` (see that file's own tests).
    rate_in_customer_currency:
      w.rate_in_customer_currency === undefined ? undefined : money(w.rate_in_customer_currency, getBaseCurrency()),
    stock: num(w.qty),
    remark: str(w.remark),
    enquiry_row_no: str(w.enquiry_row_no),
    opportunity_item: str(w.opportunity_item),
  }
}

export function toSupplierQuotation(w: WireSupplierQuotation): SupplierQuotation {
  const currency = str(w.currency, getBaseCurrency())
  const items = Array.isArray(w.items) ? w.items : []
  return {
    id: str(w.name),
    rfq: toRfqLink(w.rfq),
    supplier: str(w.supplier),
    currency,
    status: toSqStatus(w.status),
    // Neither method carries a distinct "when received" timestamp separate from the document
    // itself on this verified shape — left null rather than guessed, same as `Rfq.sent_at`
    // being the one honest timestamp `toRfq` has to offer.
    received_at: null,
    items: items.map((row) => toSupplierQuotationItem(row, currency)),
    grand_total: w.grand_total === undefined ? undefined : money(w.grand_total, currency),
  }
}

/**
 * `get_supplier_quotations`' payload is a **bare array**, not `{sqs: [...]}`.
 *
 * Measured live 2026-08-08 against `rfqdev.etoserp.com`:
 *
 * ```
 * GET supplier_quotation.get_supplier_quotations?enquiry=ENQ-2026-00018
 *   {"message":[{"name":"SQ-2026-00006","rfq":"RFQ-2026-00002","supplier":"AKA TRADE BUILDING TOOLS",…}, …]}
 * ```
 *
 * This read `res.sqs ?? []`, which against the real server is `undefined ?? []` — **an empty list,
 * every single time**. ENQ-2026-00018 has FIVE real supplier quotations against RFQ-2026-00002,
 * with items; the screen showed "1 line sent — nothing back · Awaiting" and offered to remind a
 * supplier who had quoted five times. The comparison workbench is fed from the same data.
 *
 * This is the identical defect `useEnquiries.ts`'s `readRfqList` documents for `rfq.get_rfqs` —
 * same wrapper assumption, one file over, not learned from. It throws rather than defaulting to
 * `[]` for the same reason that one does: "no quotations exist" is what drives Awaiting, Remind,
 * and an empty comparison, so an unreadable response must never be mistaken for it.
 */
export function readSqList(res: unknown, context: string): WireSupplierQuotation[] {
  if (Array.isArray(res)) return res as WireSupplierQuotation[]
  // Tolerated because the mock shipped this shape and a server could yet adopt it — but only when
  // the value really is a list.
  const wrapped = (res as { sqs?: unknown } | null | undefined)?.sqs
  if (Array.isArray(wrapped)) return wrapped as WireSupplierQuotation[]
  throw new Error(
    `supplier_quotation.get_supplier_quotations did not return a list for ${context} — got ${
      res === null || res === undefined ? String(res) : typeof res
    }. Refusing to treat an unreadable response as "no quotations have come back".`,
  )
}

export function useSupplierQuotations(enquiryId: string | null | undefined, options?: { poll?: boolean }) {
  return useQuery({
    queryKey: qk.supplierQuotations(enquiryId ?? ''),
    queryFn: async () => {
      const res = await callGet<unknown>('supplier_quotation', 'get_supplier_quotations', {
        enquiry: enquiryId,
      })
      return readSqList(res, enquiryId ?? '(no enquiry)').map(toSupplierQuotation)
    },
    enabled: !!enquiryId,
    refetchInterval: options?.poll === false ? false : SQ_POLL_MS,
  })
}

/** A single reply by name — e.g. for a detail view opened from elsewhere. Not used by
 * `SupplierQuotationsPage` today (it reads the whole list), kept for a caller that only has the
 * one id in hand rather than forcing every reader through the list endpoint. */
export function useSupplierQuotation(name: string | null | undefined) {
  return useQuery({
    queryKey: qk.supplierQuotation(name ?? ''),
    queryFn: async () =>
      toSupplierQuotation(await callGet<WireSupplierQuotation>('supplier_quotation', 'get_supplier_quotation', { name })),
    enabled: !!name,
  })
}

/* ============================================================================================
 * supplier_quotation.create_supplier_quotation
 * ============================================================================================ */

interface WireCreateSupplierQuotationResult {
  name?: string
}

async function createSupplierQuotation(payload: CreateSupplierQuotationPayload): Promise<string> {
  const w = await callPost<WireCreateSupplierQuotationResult>('supplier_quotation', 'create_supplier_quotation', {
    data: payload,
  })
  return str(w?.name)
}

export function useCreateSupplierQuotation(options?: {
  onSuccess?: (id: string, payload: CreateSupplierQuotationPayload) => void
  /** The payload travels with the failure so a caller can retry it — the duplicate case (2026-09-19). */
  onError?: (error: BosunApiError, payload: CreateSupplierQuotationPayload) => void
}) {
  const queryClient = useQueryClient()
  return useMutation<string, BosunApiError, CreateSupplierQuotationPayload>({
    mutationFn: createSupplierQuotation,
    onSuccess: (id, payload) => {
      void queryClient.invalidateQueries({ queryKey: qk.supplierQuotations(payload.enquiry) })
      /*
        Recording a quotation changes three screens, not one. Without these the Requirements board
        kept showing `0/1 replied` for an enquiry whose quotation had just been recorded —
        reported 2026-08-13 — because nothing told the board's reply data to re-read.

        `boardReplies` is the one that matters: it now counts supplier quotations as the
        authoritative "did they come back?" signal, so it must be refetched the moment one exists.
        The RFQ list is invalidated too, since the server may restatus the RFQ to `Replied`.
      */
      void queryClient.invalidateQueries({ queryKey: qk.rfqs(payload.enquiry) })
      void queryClient.invalidateQueries({ queryKey: ['board-replies'] })
      void queryClient.invalidateQueries({ queryKey: ['enquiries'] })
      /*
        The price just filed IS the past price the rest of the app looks up by item code —
        `useItemQuoteHistory` reads `Supplier Quotation Item` filtered on `item_code` alone, and
        `usePurchaseHistory` (the revision screen's supplier picker, and `SupplierPickerModal`)
        delegates to it. Without this the purchaser records a price on the revision screen,
        reopens the picker for the same item, and is told again that nobody has ever quoted it.
        Invalidated across every item, since one quotation prices many.
      */
      void queryClient.invalidateQueries({ queryKey: ['item-quote-history'] })
      options?.onSuccess?.(id, payload)
    },
    onError: (error, payload) => options?.onError?.(error, payload),
  })
}

/* ============================================================================================
 * supplier_quotation.parse_supplier_reply
 * ============================================================================================ */

interface WireParseSupplierReplyResult {
  supplier_quotation?: string | null
  matched?: number
  unmatched?: number
  uom_warnings?: unknown
}

function toParseSupplierReplyResult(w: WireParseSupplierReplyResult): ParseSupplierReplyResult {
  return {
    supplier_quotation: str(w.supplier_quotation) || null,
    matched: num(w.matched),
    unmatched: num(w.unmatched),
    uom_warnings: Array.isArray(w.uom_warnings) ? w.uom_warnings.map(String) : [],
  }
}

async function parseSupplierReply(payload: ParseSupplierReplyPayload): Promise<ParseSupplierReplyResult> {
  const w = await callPost<WireParseSupplierReplyResult>('supplier_quotation', 'parse_supplier_reply', payload)
  return toParseSupplierReplyResult(w)
}

/**
 * A manual "parse this reply now" trigger — the scheduler (`process_supplier_reply_emails`)
 * parses replies on its own every two minutes, so this is for the case where a purchaser wants
 * one handled immediately rather than waiting, not a replacement for that loop. Nothing here
 * polls; invalidating the SQ list on success is enough for a newly-parsed reply to show up the
 * next time `useSupplierQuotations` refetches (its own poll, or this invalidation, whichever
 * comes first).
 *
 * The result is handed back WHOLE — `matched`, `unmatched` and `uom_warnings` all reach the
 * caller. `unmatched` above zero means the supplier priced lines the parser could not tie back
 * to an enquiry item; dropping that count here would lose part of a real supplier quote with no
 * trace of it ever having existed.
 */
export function useParseSupplierReply(
  enquiryId: string,
  options?: {
    onSuccess?: (data: ParseSupplierReplyResult, payload: ParseSupplierReplyPayload) => void
    onError?: (error: BosunApiError) => void
  },
) {
  const queryClient = useQueryClient()
  return useMutation<ParseSupplierReplyResult, BosunApiError, ParseSupplierReplyPayload>({
    mutationFn: parseSupplierReply,
    onSuccess: (data, payload) => {
      void queryClient.invalidateQueries({ queryKey: qk.supplierQuotations(enquiryId) })
      // A parsed reply moves BOTH statuses server-side: the RFQ to `Replied` (confirmed live —
      // an RFQ with quotations reads `Replied`, one without reads `Sent`), and, once `D13` ships,
      // the enquiry to `Quotes Received`. Neither reaches the Requirements board or the RFQ list
      // without these, so the screen that says what needs attention would keep showing the
      // pre-parse state.
      void queryClient.invalidateQueries({ queryKey: qk.rfqs(enquiryId) })
      void queryClient.invalidateQueries({ queryKey: ['enquiries'] })
      options?.onSuccess?.(data, payload)
    },
    onError: (error) => options?.onError?.(error),
  })
}

/* ---------- test-only export ---------- */

export const __test__ = { createSupplierQuotation, parseSupplierReply, toParseSupplierReplyResult }
